import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Phone, Star } from "lucide-react";
import { useScrollTo } from "@/lib/smooth-scroll";
import { CONTACT, HERO_IMG, TAGLINE } from "@/data";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = () => {
  const scrollTo = useScrollTo();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.55, 0.9]);

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative h-[100svh] w-full overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Voiture de sport rouge en studio detailing"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-black" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-black/60" />

      <div className="relative z-10 mx-auto max-w-[1400px] h-full px-5 sm:px-8 flex flex-col justify-end pb-16 sm:pb-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7 }}
          className="mb-6 flex items-center gap-3 font-mono2 text-[11px] uppercase tracking-[0.25em] text-[var(--txt-1)]"
        >
          <span className="flex items-center gap-1 text-white">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={12} className="fill-[var(--blue)] text-[var(--blue)]" />
            ))}
          </span>
          <span>Lavage &amp; Detailing · Marseille</span>
        </motion.div>

        <h1 className="font-display font-extrabold uppercase leading-[0.9] tracking-tighter text-[15vw] sm:text-[11vw] lg:text-[9rem]">
          <span className="block overflow-hidden">
            <motion.span custom={0} variants={line} initial="hidden" animate="show" className="block text-white">
              Speed Auto
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span custom={1} variants={line} initial="hidden" animate="show" className="block text-[var(--blue)]">
              Lavage
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-4 font-display italic font-light text-lg sm:text-2xl text-[var(--txt-1)]"
        >
          {TAGLINE}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 flex flex-col sm:flex-row sm:items-end gap-6 sm:justify-between"
        >
          <p className="max-w-md text-[var(--txt-1)] text-base leading-relaxed">
            Votre voiture, transformée. Lavage main, detailing et protection céramique
            par des passionnés — réservez votre créneau en quelques secondes.
          </p>
          <div className="flex items-center gap-3">
            <button
              data-testid="hero-reserve-btn"
              onClick={() => scrollTo("#reservation")}
              className="group inline-flex items-center gap-3 bg-white text-black font-mono2 text-xs font-bold uppercase tracking-[0.18em] px-7 py-4 rounded-sm hover:bg-[var(--blue)] hover:text-white transition-colors duration-300"
            >
              Réserver
              <ArrowDown size={15} className="hover-arrow -rotate-90" />
            </button>
            <a
              data-testid="hero-call-btn"
              href={`tel:${CONTACT.phoneRaw}`}
              className="inline-flex items-center gap-2 border border-white/25 text-white font-mono2 text-xs uppercase tracking-[0.18em] px-6 py-4 rounded-sm hover:border-white/60 transition-colors duration-300"
            >
              <Phone size={14} /> Appeler
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
